// ---------------------------------------------------------------------------
// GEO JOB-ALERT SETTINGS — single source of truth (Phase 2, 2026-10-09).
// ---------------------------------------------------------------------------
// Server-side matching lives in the `job-alerts-digest` edge function and the
// `mkt_alert_geo_preview` RPC. Both apply the SAME rules documented here:
//
//   * Nothing configured            -> statewide (legacy trade + state match).
//   * Radius + recognised ZIP       -> projects whose ZIP centre is within
//                                      25 / 50 / 100 miles (crosses state lines).
//   * Service-area cities           -> projects in those cities (within the
//                                      alert's state). With a radius they are
//                                      ADDED to the radius; with Statewide they
//                                      LIMIT the alert to those cities.
//   * ZIP not recognised / project has no usable location -> safe fallback to
//     statewide matching, so nobody silently stops receiving alerts.
//
// PRIVACY: homeowner ZIPs/coordinates live only in mkt_listing_geo, which no
// browser session can read. The browser only ever receives a COUNT.
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';
import type { Lang } from '@/data/constants';

/** null = Statewide. */
export type RadiusMiles = 25 | 50 | 100 | null;
export const RADIUS_OPTIONS: { value: RadiusMiles; en: string; es: string }[] = [
  { value: null, en: 'Statewide', es: 'Todo el estado' },
  { value: 25, en: 'Within 25 miles', es: 'Dentro de 25 millas' },
  { value: 50, en: 'Within 50 miles', es: 'Dentro de 50 millas' },
  { value: 100, en: 'Within 100 miles', es: 'Dentro de 100 millas' },
];
export const MAX_SERVICE_CITIES = 25;

export interface GeoSettings {
  zip: string | null;
  radius_miles: RadiusMiles;
  service_cities: string[];
}
export const EMPTY_GEO: GeoSettings = { zip: null, radius_miles: null, service_cities: [] };

export const normalizeZip = (raw: string | null | undefined): string | null => {
  const d = (raw || '').replace(/\D/g, '').slice(0, 5);
  return d.length === 5 ? d : null;
};

/** Splits "Fontana, Riverside; Ontario" into a de-duplicated, trimmed list. */
export const parseCities = (raw: string | string[] | null | undefined): string[] => {
  const parts = Array.isArray(raw) ? raw : (raw || '').split(/[,;\n]/);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const c = p.trim().replace(/\s+/g, ' ');
    if (!c || c.length > 60 || seen.has(c.toLowerCase())) continue;
    seen.add(c.toLowerCase());
    out.push(c);
    if (out.length >= MAX_SERVICE_CITIES) break;
  }
  return out;
};

/** Is this ZIP in our US ZIP-centroid reference table? (public Census data) */
export const isKnownZip = async (zip: string | null): Promise<boolean> => {
  const z = normalizeZip(zip);
  if (!z) return false;
  const { data } = await supabase.from('mkt_zip_centroids').select('zip').eq('zip', z).maybeSingle();
  return !!data;
};

// ---------------------------------------------------------------------------
// PROJECT ZIP VALIDATION (2026-10-09 geo cleanup).
// Mirrors the database rule in mkt_zip_is_placeholder() / the
// trg_mkt_listings_validate_zip trigger, which is the real enforcement — this
// client copy only gives the poster an instant, friendly message.
//   * must be 5 digits (ZIP+4 "12345-6789" accepted)
//   * 00000–00500 are never real USPS ZIPs (lowest real ZIP is 00501)
//   * 11111 / 99999 style repeats are rejected unless the Census table knows
//     them (44444 is real)
//   * well-formed but not in the Census table (PO-box / single-building ZIPs
//     such as 08101) is allowed after the poster confirms it.
// ---------------------------------------------------------------------------
export const ZIP_FORMAT_RE = /^\d{5}(-?\d{4})?$/;
export const LOWEST_REAL_ZIP = 501;

export type ZipCheck = 'empty' | 'format' | 'placeholder' | 'unknown' | 'ok';

/** Synchronous part of the rule (no network). */
export const zipShapeCheck = (raw: string | null | undefined): Exclude<ZipCheck, 'unknown' | 'ok'> | 'shape_ok' => {
  const v = (raw || '').trim();
  if (!v) return 'empty';
  if (!ZIP_FORMAT_RE.test(v)) return 'format';
  if (Number(v.slice(0, 5)) < LOWEST_REAL_ZIP) return 'placeholder';
  return 'shape_ok';
};

/** Full check, including the Census reference lookup. */
export const checkListingZip = async (raw: string | null | undefined): Promise<ZipCheck> => {
  const shape = zipShapeCheck(raw);
  if (shape !== 'shape_ok') return shape;
  const z5 = (raw || '').trim().slice(0, 5);
  const known = await isKnownZip(z5);
  if (known) return 'ok';
  if (/^(\d)\1{4}$/.test(z5)) return 'placeholder';
  return 'unknown';
};

export type ZipCopyKey = 'required' | 'format' | 'placeholder' | 'unknown' | 'confirm' | 'legacy' | 'stateMismatch';
export const ZIP_COPY: Record<ZipCopyKey, { en: string; es: string }> = {
  required: { en: 'Enter the 5-digit ZIP code where the work will be done.', es: 'Ingrese el código postal de 5 dígitos donde se hará el trabajo.' },
  format: { en: 'ZIP code must be 5 digits (for example 21044).', es: 'El código postal debe tener 5 dígitos (por ejemplo 21044).' },
  placeholder: { en: 'That is not a real US ZIP code. Please enter the actual ZIP code of the project.', es: 'Ese no es un código postal real de EE. UU. Ingrese el código postal real del proyecto.' },
  unknown: { en: 'We could not find this ZIP in our US ZIP list. If it is a PO-box or single-building ZIP, confirm it below.', es: 'No encontramos este código postal en nuestra lista. Si es de apartado postal o de un solo edificio, confírmelo abajo.' },
  confirm: { en: 'Yes, this ZIP code is correct for the project location.', es: 'Sí, este código postal es correcto para el proyecto.' },
  legacy: { en: 'This project has a placeholder ZIP code on file. Please replace it with the real ZIP so nearby contractors can find it.', es: 'Este proyecto tiene un código postal provisional. Reemplácelo por el real para que los contratistas cercanos lo encuentren.' },
  stateMismatch: { en: 'This ZIP code belongs to a different state than the one selected. Check the ZIP or the state.', es: 'Este código postal pertenece a un estado distinto del seleccionado. Revise el código postal o el estado.' },
};

/** Maps a database / edge error code (mkt_listing_zip_problem, trigger) to a ZIP_COPY key. */
export const zipErrorKey = (codeOrMessage: string | null | undefined): ZipCopyKey | null => {
  const m = (codeOrMessage || '').toLowerCase();
  if (m.includes('zip_state_mismatch')) return 'stateMismatch';
  if (m.includes('placeholder_zip')) return 'placeholder';
  if (m.includes('invalid_zip')) return 'format';
  if (m.includes('zip_required')) return 'required';
  return null;
};

export type GeoMode = 'statewide' | 'radius' | 'cities';
export const geoModeOf = (g: Partial<GeoSettings>, zipKnown = true): GeoMode => {
  if (g.radius_miles && normalizeZip(g.zip) && zipKnown) return 'radius';
  if ((g.service_cities || []).length) return 'cities';
  return 'statewide';
};

/** Short human label, e.g. "Within 50 mi of 63101 + 2 cities". */
export const describeGeo = (g: Partial<GeoSettings>, lang: Lang): string => {
  const cities = g.service_cities || [];
  const es = lang === 'es';
  if (g.radius_miles && g.zip) {
    const base = es ? `Dentro de ${g.radius_miles} mi de ${g.zip}` : `Within ${g.radius_miles} mi of ${g.zip}`;
    return cities.length ? `${base} + ${cities.length} ${es ? 'ciudad(es)' : 'city(ies)'}` : base;
  }
  if (cities.length) {
    const shown = cities.slice(0, 3).join(', ') + (cities.length > 3 ? ` +${cities.length - 3}` : '');
    return es ? `Solo: ${shown}` : `Only: ${shown}`;
  }
  return es ? 'Todo el estado' : 'Statewide';
};

export interface GeoPreview {
  count: number;
  zip_given: boolean;
  zip_valid: boolean;
  radius_active: boolean;
  cities_active: boolean;
  mode: GeoMode;
  fallback_listings: number;
}

/**
 * Count-only preview of how many OPEN projects an alert would match right now,
 * computed server-side with the exact digest rules. Never returns locations.
 */
export const previewGeoMatches = async (f: {
  trades: string[];
  state: string | null;
  city: string | null;
  project_type: string | null;
  min_budget: number | null;
} & GeoSettings): Promise<GeoPreview | null> => {
  const { data, error } = await supabase.rpc('mkt_alert_geo_preview', {
    p_trades: f.trades || [],
    p_state: f.state || null,
    p_city: f.city?.trim() || null,
    p_project_type: f.project_type || null,
    p_min_budget: f.min_budget,
    p_zip: normalizeZip(f.zip),
    p_radius: f.radius_miles,
    p_cities: f.service_cities || [],
  });
  if (error || !data) return null;
  return data as GeoPreview;
};
